import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useState } from "react";
import { MdChevronRight } from "react-icons/md";

import {
  billingAndPaymentsQuestions,
  ordersAndProjectsQuestions,
  supportAndCommunicationQuestions,
  teamAndBusinessAccountsQuestions,
} from "../constants/questions";
import { resourceCards } from "../constants/cards";
import ProjectsIcon from "assets/img/support/projects-Icon.svg";

type Step = {
  text: string;
  bold?: string;
  suffix?: string;
  bold2?: string;
  suffix2?: string;
};

export default function HelpfulResources() {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [selectedSection, setSelectedSection] = useState<
    "orders" | "billing" | "team" | "support"
  >("orders");

  const billingCard = resourceCards.find((c) => c.id === "billing");
  const teamCard = resourceCards.find((c) => c.id === "team");
  const supportCard = resourceCards.find((c) => c.id === "support");

  const gridCards = [
    {
      id: "orders",
      icon: ProjectsIcon,
      title: "Orders and projects",
      description: "Everything you need to manage your translation projects.",
    },
    ...resourceCards,
  ];

  const sectionQuestions =
    selectedSection === "orders"
      ? ordersAndProjectsQuestions
      : selectedSection === "billing"
        ? billingAndPaymentsQuestions
        : selectedSection === "team"
          ? teamAndBusinessAccountsQuestions
          : supportAndCommunicationQuestions;

  const [selectedQuestion, setSelectedQuestion] = useState(
    sectionQuestions[0].id,
  );
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  const activeQuestion = sectionQuestions.find(
    (q) => q.id === selectedQuestion,
  );

  const scrollToHelpfulResources = () => {
    document.getElementById("helpful-resources")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const getQuestionsForCard = (cardId: string) => {
    if (cardId === "billing") return billingAndPaymentsQuestions;
    if (cardId === "team") return teamAndBusinessAccountsQuestions;
    if (cardId === "support") return supportAndCommunicationQuestions;
    if (cardId === "orders") return ordersAndProjectsQuestions;
    return [];
  };

  const handleLearnMoreClick = (cardId: string) => {
    if (isMobile) {
      setExpandedCardId(expandedCardId === cardId ? null : cardId);
      return;
    }

    if (cardId === "billing") {
      setSelectedSection("billing");
      setSelectedQuestion(billingAndPaymentsQuestions[0].id);
    } else if (cardId === "team") {
      setSelectedSection("team");
      setSelectedQuestion(teamAndBusinessAccountsQuestions[0].id);
    } else if (cardId === "support") {
      setSelectedSection("support");
      setSelectedQuestion(supportAndCommunicationQuestions[0].id);
    } else {
      setSelectedSection("orders");
      setSelectedQuestion(ordersAndProjectsQuestions[0].id);
    }

    scrollToHelpfulResources();
  };

  return (
    <Box id="helpful-resources">
      {/* Mobile title block with grey background */}
      <Box
        display={{ base: "block", md: "none" }}
        bg="#F5F7FA"
        p="16px"
        border="1px solid #E6ECF3"
        borderRadius="12px"
        mb="16px"
      >
        <Text fontSize="20px" fontWeight="600" color="#0B0E10" mb="4px">
          Helpful resources
        </Text>
        <Text fontSize="14px" color="#0B0E10">
          Explore quick guides and common questions to help you manage your
          translations and account.
        </Text>
      </Box>

      {/* Desktop container with grey background */}
      <Box
        display={{ base: "none", md: "block" }}
        bg="#F5F7FA"
        p="24px"
        border="1px solid #E6ECF3"
        borderRadius="12px"
      >
        {/* Header */}
        <Text fontSize="20px" fontWeight="600" color="#0B0E10" mb="4px">
          Helpful resources
        </Text>
        <Text fontSize="14px" color="#0B0E10" mb="24px">
          Explore quick guides and common questions to help you manage your
          translations and account.
        </Text>

        {/* Orders and projects section - hidden on mobile */}
        <Box
          display={{ base: "none", md: "block" }}
          bg="#fff"
          borderRadius="24px"
          border="1px solid #E6ECF3"
          p="22px"
          mb="24px"
        >
          {/* Section icon and title */}
          <Flex align="center" gap="12px" mb="4px">
            <Box
              w="40px"
              h="40px"
              borderRadius="8px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="img"
                src={
                  selectedSection === "orders"
                    ? ProjectsIcon
                    : selectedSection === "billing"
                      ? billingCard?.icon
                      : selectedSection === "team"
                        ? teamCard?.icon
                        : supportCard?.icon
                }
                w="48px"
                h="48px"
              />
            </Box>
          </Flex>
          <Text fontSize="16px" fontWeight="600" color="#0B0E10" mt="8px">
            {selectedSection === "orders"
              ? "Orders and projects"
              : selectedSection === "billing"
                ? billingCard?.title
                : selectedSection === "team"
                  ? teamCard?.title
                  : supportCard?.title}
          </Text>
          <Text
            fontSize="14px"
            color="#6B7280"
            mb="24px"
            pb="24px"
            borderBottom="1px solid #D9D9D9"
          >
            {selectedSection === "orders"
              ? "Everything you need to manage your translation projects."
              : selectedSection === "billing"
                ? billingCard?.description
                : selectedSection === "team"
                  ? teamCard?.description
                  : supportCard?.description}
          </Text>

          {/* Questions and content - Mobile Accordion */}
          {isMobile ? (
            <Accordion allowToggle>
              {sectionQuestions.map((question) => (
                <AccordionItem
                  key={question.id}
                  border="none"
                  borderRadius="8px"
                  mb="12px"
                  overflow="hidden"
                >
                  <AccordionButton
                    px="16px"
                    py="14px"
                    bg="#F5F7FA"
                    _hover={{ bg: "#F5F7FA" }}
                    _expanded={{ bg: "#F5F7FA" }}
                  >
                    <Text
                      flex="1"
                      textAlign="left"
                      fontSize="16px"
                      fontWeight="500"
                      color="#0B0E10"
                    >
                      {question.title}
                    </Text>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel px="16px" py="16px" bg="#FFFFFF">
                    <Text
                      fontSize="14px"
                      fontWeight="400"
                      color="#000000"
                      mb="16px"
                    >
                      {question.content.description}
                    </Text>
                    <Box as="ol" pl="20px" mb="16px">
                      {(question.content.steps as Step[]).map((step, index) => (
                        <Box
                          as="li"
                          key={index}
                          fontSize="14px"
                          color="#000000"
                          mb="10px"
                        >
                          {step.text}
                          {step.bold && (
                            <Text as="span" fontWeight="700">
                              {step.bold}
                            </Text>
                          )}
                          {step.suffix && <Text as="span">{step.suffix}</Text>}
                          {step.bold2 && (
                            <Text as="span" fontWeight="700">
                              {step.bold2}
                            </Text>
                          )}
                          {step.suffix2 && (
                            <Text as="span">{step.suffix2}</Text>
                          )}
                        </Box>
                      ))}
                    </Box>
                    <Box bg="#FEF9E7" borderRadius="8px" p="12px">
                      <Text
                        fontSize="12px"
                        fontWeight="600"
                        color="#B54708"
                        mb="4px"
                      >
                        Additional tip:
                      </Text>
                      <Text fontSize="12px" color="#B54708">
                        {question.content.tip}
                      </Text>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            /* Desktop layout */
            <Flex gap="32px" flexWrap="wrap">
              {/* Questions list */}
              <Box minW="395px" flex="1">
                {sectionQuestions.map((question) => (
                  <Box
                    key={question.id}
                    px="16px"
                    py="12.5px"
                    cursor="pointer"
                    bg={
                      selectedQuestion === question.id ? "#EBF5FF" : "#F5F7FA"
                    }
                    border={
                      selectedQuestion === question.id
                        ? "1px solid #2F80ED"
                        : "1px solid #F5F7FA"
                    }
                    borderRadius="8px"
                    mb="16px"
                    onClick={() => setSelectedQuestion(question.id)}
                    _hover={{
                      bg:
                        selectedQuestion === question.id
                          ? "#EBF5FF"
                          : "#F9FAFB",
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      <Text
                        fontSize="16px"
                        fontWeight="400"
                        color={
                          selectedQuestion === question.id
                            ? "#2F80ED"
                            : "#0B0E10"
                        }
                      >
                        {question.title}
                      </Text>
                      <Icon
                        as={MdChevronRight}
                        w="20px"
                        h="20px"
                        color="#0B0E10"
                      />
                    </Flex>
                  </Box>
                ))}
              </Box>

              {/* Content area */}
              {activeQuestion && (
                <Box flex="1" minW="400px" bg="white" borderRadius="8px">
                  <Text
                    fontSize="18px"
                    fontWeight="600"
                    color="#0B0E10"
                    mb="16px"
                  >
                    {activeQuestion.title}
                  </Text>
                  <Text
                    fontSize="16px"
                    fontWeight="400"
                    color="#000000"
                    mb="20px"
                  >
                    {activeQuestion.content.description}
                  </Text>
                  <Box as="ol" pl="22px" mb="24px">
                    {(activeQuestion.content.steps as Step[]).map(
                      (step, index) => (
                        <Box
                          as="li"
                          key={index}
                          fontSize="16px"
                          color="#000000"
                          mb="12px"
                        >
                          {step.text}
                          {step.bold && (
                            <Text as="span" fontWeight="900">
                              {step.bold}
                            </Text>
                          )}
                          {step.suffix && <Text as="span">{step.suffix}</Text>}
                          {step.bold2 && (
                            <Text as="span" fontWeight="900">
                              {step.bold2}
                            </Text>
                          )}
                          {step.suffix2 && (
                            <Text as="span">{step.suffix2}</Text>
                          )}
                        </Box>
                      ),
                    )}
                  </Box>
                  {/* Tip box */}
                  <Box bg="#FEF9E7" borderRadius="8px" p="16px">
                    <Text
                      fontSize="14px"
                      fontWeight="600"
                      color="#B54708"
                      mb="4px"
                    >
                      Additional tip:
                    </Text>
                    <Text fontSize="14px" color="#B54708">
                      {activeQuestion.content.tip}
                    </Text>
                  </Box>
                </Box>
              )}
            </Flex>
          )}
        </Box>

        {/* Resource cards - desktop */}
        <Grid templateColumns="repeat(auto-fit, minmax(296px, 1fr))" gap="16px">
          {gridCards.map((card) => {
            const cardQuestions = getQuestionsForCard(card.id);
            const isExpanded = expandedCardId === card.id;

            return (
              <GridItem key={card.id}>
                <Box
                  bg="#FFFFFF"
                  borderRadius="12px"
                  border="1px solid #E6ECF3"
                  p="24px"
                  h={{ base: "auto", md: "223px" }}
                  minH={{ base: "auto", md: "223px" }}
                  transition="all 0.2s ease"
                  _hover={{
                    boxShadow: "0 8px 24px rgba(47, 128, 237, 0.35)",
                  }}
                  onClick={() => handleLearnMoreClick(card.id)}
                  cursor="pointer"
                >
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="8px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb="8px"
                  >
                    <Box as="img" src={card.icon} w="40px" h="40px" />
                  </Box>
                  <Text
                    fontSize="18px"
                    fontWeight="600"
                    color="#0B0E10"
                    mb="8px"
                  >
                    {card.title}
                  </Text>
                  <Text
                    fontSize="14px"
                    color="#505E70"
                    mb={{ base: "16px", md: "32px" }}
                  >
                    {card.description}
                  </Text>
                  <Flex
                    align="center"
                    gap="4px"
                    color="#2F80ED"
                    p="0"
                    cursor="pointer"
                  >
                    <Text
                      fontSize="16px"
                      fontWeight="600"
                      _hover={{ textDecoration: "underline" }}
                    >
                      {isMobile && isExpanded ? "Show less" : "Learn more"}
                    </Text>
                    <Icon
                      as={MdChevronRight}
                      w="18px"
                      h="18px"
                      transform={
                        isMobile && isExpanded ? "rotate(90deg)" : "none"
                      }
                      transition="transform 0.2s"
                    />
                  </Flex>

                  {/* Expanded content for mobile */}
                  {isMobile && isExpanded && cardQuestions.length > 0 && (
                    <Box mt="16px" pt="16px" borderTop="1px solid #E6ECF3">
                      <Accordion allowToggle>
                        {cardQuestions.map((question) => (
                          <AccordionItem
                            key={question.id}
                            border="none"
                            borderRadius="8px"
                            mb="8px"
                            overflow="hidden"
                          >
                            <AccordionButton
                              px="12px"
                              py="12px"
                              bg="#F5F7FA"
                              _hover={{ bg: "#F5F7FA" }}
                              _expanded={{ bg: "#F5F7FA" }}
                            >
                              <Text
                                flex="1"
                                textAlign="left"
                                fontSize="14px"
                                fontWeight="500"
                                color="#0B0E10"
                              >
                                {question.title}
                              </Text>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px="12px" py="12px" bg="#FFFFFF">
                              <Text fontSize="13px" color="#000000" mb="12px">
                                {question.content.description}
                              </Text>
                              <Box as="ol" pl="18px" mb="12px">
                                {(question.content.steps as Step[]).map(
                                  (step, index) => (
                                    <Box
                                      as="li"
                                      key={index}
                                      fontSize="13px"
                                      color="#000000"
                                      mb="8px"
                                    >
                                      {step.text}
                                      {step.bold && (
                                        <Text as="span" fontWeight="700">
                                          {step.bold}
                                        </Text>
                                      )}
                                      {step.suffix && (
                                        <Text as="span">{step.suffix}</Text>
                                      )}
                                      {step.bold2 && (
                                        <Text as="span" fontWeight="700">
                                          {step.bold2}
                                        </Text>
                                      )}
                                      {step.suffix2 && (
                                        <Text as="span">{step.suffix2}</Text>
                                      )}
                                    </Box>
                                  ),
                                )}
                              </Box>
                              <Box bg="#FEF9E7" borderRadius="6px" p="10px">
                                <Text
                                  fontSize="11px"
                                  fontWeight="600"
                                  color="#B54708"
                                  mb="2px"
                                >
                                  Tip:
                                </Text>
                                <Text fontSize="11px" color="#B54708">
                                  {question.content.tip}
                                </Text>
                              </Box>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </Box>
                  )}
                </Box>
              </GridItem>
            );
          })}
        </Grid>
      </Box>

      {/* Resource cards - mobile full width */}
      <Grid
        display={{ base: "grid", md: "none" }}
        templateColumns="1fr"
        gap="16px"
      >
        {gridCards.map((card) => {
          const cardQuestions = getQuestionsForCard(card.id);
          const isExpanded = expandedCardId === card.id;

          return (
            <GridItem key={card.id}>
              <Box
                bg="#FFFFFF"
                borderRadius="12px"
                border="1px solid #E6ECF3"
                p="24px"
                _hover={{
                  boxShadow: "0 8px 24px rgba(47, 128, 237, 0.35)",
                }}
                onClick={() => handleLearnMoreClick(card.id)}
                cursor="pointer"
              >
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="8px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb="8px"
                >
                  <Box as="img" src={card.icon} w="40px" h="40px" />
                </Box>
                <Text fontSize="18px" fontWeight="600" color="#0B0E10" mb="8px">
                  {card.title}
                </Text>
                <Text fontSize="14px" color="#505E70" mb="16px">
                  {card.description}
                </Text>
                <Flex
                  align="center"
                  gap="4px"
                  color="#2F80ED"
                  p="0"
                  cursor="pointer"
                >
                  <Text
                    fontSize="16px"
                    fontWeight="600"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {isExpanded ? "Show less" : "Learn more"}
                  </Text>
                  <Icon
                    as={MdChevronRight}
                    w="18px"
                    h="18px"
                    transform={isExpanded ? "rotate(90deg)" : "none"}
                    transition="transform 0.2s"
                  />
                </Flex>

                {/* Expanded content for mobile */}
                {isExpanded && cardQuestions.length > 0 && (
                  <Box mt="16px" pt="16px" borderTop="1px solid #E6ECF3">
                    <Accordion allowToggle>
                      {cardQuestions.map((question) => (
                        <AccordionItem
                          key={question.id}
                          border="none"
                          borderRadius="8px"
                          mb="8px"
                          overflow="hidden"
                        >
                          <AccordionButton
                            px="12px"
                            py="12px"
                            bg="#F5F7FA"
                            _hover={{ bg: "#F5F7FA" }}
                            _expanded={{ bg: "#F5F7FA" }}
                          >
                            <Text
                              flex="1"
                              textAlign="left"
                              fontSize="14px"
                              fontWeight="500"
                              color="#0B0E10"
                            >
                              {question.title}
                            </Text>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel px="12px" py="12px" bg="#FFFFFF">
                            <Text fontSize="13px" color="#000000" mb="12px">
                              {question.content.description}
                            </Text>
                            <Box as="ol" pl="18px" mb="12px">
                              {(question.content.steps as Step[]).map(
                                (step, index) => (
                                  <Box
                                    as="li"
                                    key={index}
                                    fontSize="13px"
                                    color="#000000"
                                    mb="8px"
                                  >
                                    {step.text}
                                    {step.bold && (
                                      <Text as="span" fontWeight="700">
                                        {step.bold}
                                      </Text>
                                    )}
                                    {step.suffix && (
                                      <Text as="span">{step.suffix}</Text>
                                    )}
                                    {step.bold2 && (
                                      <Text as="span" fontWeight="700">
                                        {step.bold2}
                                      </Text>
                                    )}
                                    {step.suffix2 && (
                                      <Text as="span">{step.suffix2}</Text>
                                    )}
                                  </Box>
                                ),
                              )}
                            </Box>
                            <Box bg="#FEF9E7" borderRadius="6px" p="10px">
                              <Text
                                fontSize="11px"
                                fontWeight="600"
                                color="#B54708"
                                mb="2px"
                              >
                                Tip:
                              </Text>
                              <Text fontSize="11px" color="#B54708">
                                {question.content.tip}
                              </Text>
                            </Box>
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </Box>
                )}
              </Box>
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
}
